import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import Client from "lib/api/Client";
import { InfiniteLoader, AutoSizer, Column, Table } from "react-virtualized";
import "react-virtualized/styles.css";
import PageLayout from "components/layouts/PageLayout";
import Button from "components/common/Button";
import ButtonGroup from "components/common/ButtonGroup";
import { CompanyContext } from "lib/context/CompanyContext";
import { exportRecordsAsCSV } from "lib/utils/csv";
import clientListCache from "lib/utils/clientListCache";
import { CLOUD_TYPE_SHORT_LABELS, parseCloudType, parseCloudLogins } from "lib/utils/license";
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { filterBarStyles } from "components/common/FilterBar/styles";

function getCloudType(rowData) {
  return rowData.cloud_type !== undefined ? rowData.cloud_type : parseCloudType(rowData.permissions?.celicense);
}

function getCloudUsers(rowData) {
  return rowData.cloud_users !== undefined ? rowData.cloud_users : parseCloudLogins(rowData.permissions?.celicense);
}

const PILL_STYLE = {
  padding: "3px 10px",
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 500,
  whiteSpace: "nowrap",
};

const STATUS_COLORS = {
  Enabled: { bg: "#d1fae5", color: "#065f46" },
  Disabled: { bg: "#f3f4f6", color: "#4b5563" },
};

function StatusBadge({ enabled }) {
  const label = enabled ? "Enabled" : "Disabled";
  const colors = STATUS_COLORS[label];
  return (
    <span style={{ ...PILL_STYLE, backgroundColor: colors.bg, color: colors.color }}>
      {label}
    </span>
  );
}

function getSortValue(rowData, sortKey) {
  switch (sortKey) {
    case "name": return (rowData.name || "").toLowerCase();
    case "code": return (rowData.code || "").toLowerCase();
    case "state": return (rowData.permissions?.state || "").toLowerCase();
    case "billtype": return (rowData.permissions?.billtype || "").toLowerCase();
    case "cloud_type": return (getCloudType(rowData) || "").toLowerCase();
    case "cloud_users": return getCloudUsers(rowData) ?? -1;
    case "desktop_version": return rowData.desktop_version || "";
    case "license_date": return rowData.permissions?.license_date || "";
    case "status": return rowData.enabled ? 1 : 0;
    case "created_at": return rowData.created_at || "";
    case "updated_at": return rowData.updated_at || "";
    default: return "";
  }
}

function SortableHeader({ label, sortKey, sortColumn, sortDirection, onSort }) {
  const active = sortColumn === sortKey;
  return (
    <div
      onClick={() => onSort(sortKey)}
      style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", userSelect: "none" }}
      title={`Sort by ${label}`}
    >
      {label}
      {active && (
        <span style={{ fontSize: 12 }}>{sortDirection === "asc" ? "↑" : "↓"}</span>
      )}
    </div>
  );
}

const tableStyles = `
  .clients-table .ReactVirtualized__Table__headerRow {
    background-color: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
    text-transform: uppercase;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: #6b7280;
  }
  .clients-table .ReactVirtualized__Table__headerColumn {
    display: flex;
    align-items: center;
    padding: 0 12px;
    overflow: visible !important;
  }
  .clients-table .ReactVirtualized__Table__row {
    border-bottom: 1px solid #f3f4f6;
    background: #ffffff;
    transition: background 0.1s;
  }
  .clients-table .ReactVirtualized__Table__row:hover {
    background-color: #f9fafb;
  }
  .clients-table .ReactVirtualized__Table__rowColumn {
    display: flex;
    align-items: center;
    padding: 0 12px;
    overflow: hidden;
    font-size: 14px;
    color: #111827;
  }
  .clients-table .ReactVirtualized__Grid {
    outline: none;
  }
`;

const CLIENT_LIST_STATE_KEY = "client_list_view_state";

const DEFAULT_FILTERS = {
  state: "",
  billtype: "",
  desktop_version: "",
  license_start_date: "",
  license_end_date: "",
  created_start_date: "",
  created_end_date: ""
};

function loadPersistedViewState() {
  try {
    const stored = sessionStorage.getItem(CLIENT_LIST_STATE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    // ignore malformed/missing state
  }
  return null;
}

export default function ClientList(props) {
  const { company } = useContext(CompanyContext);
  const persistedViewState = loadPersistedViewState();

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(persistedViewState?.search || "");
  const [loading, setLoading] = useState(false);

  const [recordsPerPage, setRecordsPerPage] = useState(500);
  const [copyStatus, setCopyStatus] = useState("idle"); // idle | copying | copied

  const [sortColumn, setSortColumn] = useState(persistedViewState?.sortColumn ?? null);
  const [sortDirection, setSortDirection] = useState(persistedViewState?.sortDirection || "asc");

  // Filter states
  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    ...(persistedViewState?.filters || {})
  });

  async function load(forceRefresh = false) {
    await setLoading(true);

    // Build filter object for cache comparison
    const cacheFilters = {
      company_uuid: company.uuid && company.client.uuid !== undefined ? company.client.uuid : null,
      search: search || null,
      state: filters.state || null,
      billtype: filters.billtype || null,
      desktop_version: filters.desktop_version || null,
      license_start_date: filters.license_start_date || null,
      license_end_date: filters.license_end_date || null,
      created_start_date: filters.created_start_date || null,
      created_end_date: filters.created_end_date || null
    };

    // Check cache first unless forcing refresh
    if (!forceRefresh) {
      const cached = clientListCache.get(cacheFilters);
      if (cached.fromCache && cached.records.length > 0) {
        // Reconstruct the permissions object format for UI
        const reconstitutedRecords = cached.records.map(record => ({
          ...record,
          permissions: {
            state: record.state,
            billtype: record.billtype,
            license_date: record.license_date
          },
          cloud_type: record.cloud_type,
          cloud_users: record.cloud_users
        }));
        await setRecords(reconstitutedRecords);
        await setTotal(cached.total);
        await setLoading(false);
        return;
      }
    }

    // Not in cache or forcing refresh, fetch from API
    let params = {
      offset: 0,
      limit: recordsPerPage,
    }
    if(company.uuid && company.client.uuid !== undefined){
      params.uuid = company.client.uuid;
    }
    if(search){
      params.search = search;
    }
    // Add filter parameters
    if(filters.state){
      params.state = filters.state;
    }
    if(filters.billtype){
      params.billtype = filters.billtype;
    }
    if(filters.desktop_version){
      params.desktop_version = filters.desktop_version;
    }
    if(filters.license_start_date){
      params['gte~license_date'] = filters.license_start_date;
    }
    if(filters.license_end_date){
      params['lte~license_date'] = filters.license_end_date;
    }
    if(filters.created_start_date){
      params['gte~created_at'] = filters.created_start_date;
    }
    if(filters.created_end_date){
      params['lte~created_at'] = filters.created_end_date;
    }

    let response = await Client.get(params);

    // Save to cache
    clientListCache.set(response.data, response.total, cacheFilters);

    await setRecords(response.data);
    await setTotal(response.total);
    await setLoading(false);
  }
  function isRowLoaded({ index }) {
    return !!records[index];
  }
  async function loadMoreRows({ startIndex, stopIndex }) {
    if (loading) return;

    await setLoading(true);

    // Build filter object for cache comparison
    const cacheFilters = {
      company_uuid: company.uuid && company.client.uuid !== undefined ? company.client.uuid : null,
      search: search || null,
      state: filters.state || null,
      billtype: filters.billtype || null,
      desktop_version: filters.desktop_version || null,
      license_start_date: filters.license_start_date || null,
      license_end_date: filters.license_end_date || null,
      created_start_date: filters.created_start_date || null,
      created_end_date: filters.created_end_date || null
    };

    // Check if we already have this data in cache
    const cached = clientListCache.get(cacheFilters);
    if (cached.fromCache && cached.records.length >= stopIndex) {
      // We have enough cached data, just use it
      const newData = cached.records.slice(startIndex, stopIndex + 1);
      // Reconstruct the permissions object format for UI
      const reconstitutedNewData = newData.map(record => ({
        ...record,
        permissions: {
          state: record.state,
          billtype: record.billtype,
          license_date: record.license_date
        },
        cloud_type: record.cloud_type,
        cloud_users: record.cloud_users
      }));
      setRecords(prevRecords => {
        // Only append records we don't have yet
        const existingUuids = new Set(prevRecords.map(r => r.uuid));
        const uniqueNew = reconstitutedNewData.filter(r => !existingUuids.has(r.uuid));
        return [...prevRecords, ...uniqueNew];
      });
      await setLoading(false);
      return;
    }

    // Need to fetch from API
    let params = {
      offset: startIndex,
      limit: Math.max(recordsPerPage, stopIndex - startIndex + 1)
    }
    if(search){
      params.search = search;
    }
    // Add filter parameters
    if(filters.state){
      params.state = filters.state;
    }
    if(filters.billtype){
      params.billtype = filters.billtype;
    }
    if(filters.desktop_version){
      params.desktop_version = filters.desktop_version;
    }
    if(filters.license_start_date){
      params['gte~license_date'] = filters.license_start_date;
    }
    if(filters.license_end_date){
      params['lte~license_date'] = filters.license_end_date;
    }
    if(filters.created_start_date){
      params['gte~created_at'] = filters.created_start_date;
    }
    if(filters.created_end_date){
      params['lte~created_at'] = filters.created_end_date;
    }

    let response = await Client.get(params);
    if (response.total !== total) {
      await setTotal(response.total);
    }

    const newData = response.data;

    // Append to cache
    clientListCache.append(newData, response.total, cacheFilters);

    setRecords(prevRecords => [...prevRecords, ...newData]);
    await setLoading(false);
  }

  async function fetchAllRecords() {
    const allRecords = [];
    let hasMore = true;
    let offset = 0;

    while (hasMore) {
      let params = {
        offset: offset,
        limit: 1000
        // , fields: ["uuid", "name", "code", "type", "id", "desktop_version", "expiration", "created_at", "updated_at"]
      };

      if(company.uuid && company.client.uuid !== undefined){
        params.uuid = company.client.uuid;
      }
      if(search){
        params.search = search;
      }
      // Add filter parameters
      if(filters.state){
        params.state = filters.state;
      }
      if(filters.billtype){
        params.billtype = filters.billtype;
      }
      if(filters.desktop_version){
        params.desktop_version = filters.desktop_version;
      }
      if(filters.license_date && filters.license_date_operator){
        const operatorMap = {
          'equals': 'license_date',
          'gt': 'gt~license_date',
          'lt': 'lt~license_date',
          'gte': 'gte~license_date',
          'lte': 'lte~license_date'
        };
        const paramKey = operatorMap[filters.license_date_operator];
        if(paramKey){
          params[paramKey] = filters.license_date;
        }
      }
      if(filters.created_start_date){
        params['gte~created_at'] = filters.created_start_date;
      }
      if(filters.created_end_date){
        params['lte~created_at'] = filters.created_end_date;
      }

      const response = await Client.get(params);
      allRecords.push(...response.data);

      hasMore = allRecords.length < response.total;
      offset += response.data.length;
    }

    return allRecords;
  }
  
  async function exportAllRecordsAsCSV() {
    setLoading(true); // Show loading indicator if necessary
    try {
      const allRecords = await fetchAllRecords();

      // Flatten permissions fields and reorder columns for export
      const recordsWithFlattenedPermissions = allRecords.map(record => {
        const { permissions, settings, notes, new_key, created_at, updated_at, ...otherFields } = record;
        const cloudType = getCloudType(record);
        return {
          ...otherFields,
          state: record.permissions?.state || "",
          billtype: record.permissions?.billtype || "",
          license_date: record.permissions?.license_date || "",
          cloud_type: cloudType ? (CLOUD_TYPE_SHORT_LABELS[cloudType] || cloudType) : "",
          cloud_users: getCloudUsers(record) ?? "",
          created_at,
          updated_at
        };
      });

      const columnsToSkip = ["permissions", "settings", "notes", "new_key"];

      await exportRecordsAsCSV(recordsWithFlattenedPermissions, columnsToSkip, 'client-export.csv');
    } catch (error) {
      console.error('An error occurred while fetching records:', error);
      // Handle errors as appropriate for your application
    }
    setLoading(false); // Hide loading indicator
  }

  async function copyAllUUIDsToClipboard() {
    setCopyStatus("copying");
    try {
      const allRecords = await fetchAllRecords();
      const uuids = allRecords.map(r => r.uuid).join("\n");
      await navigator.clipboard.writeText(uuids);
      setCopyStatus("copied");
      setTimeout(() => setCopyStatus("idle"), 2000);
    } catch (error) {
      console.error("Failed to copy UUIDs:", error);
      setCopyStatus("idle");
    }
  }

  function handleRefresh() {
    clientListCache.clear();
    load(true);
  }

  useEffect(() => {
    load();
    return () => { setLoading(false) };
  }, [company]);

  useEffect(() => {
    try {
      sessionStorage.setItem(CLIENT_LIST_STATE_KEY, JSON.stringify({
        search,
        filters,
        sortColumn,
        sortDirection
      }));
    } catch (error) {
      // ignore storage errors
    }
  }, [search, filters, sortColumn, sortDirection]);

  useEffect(() => {
    // Trigger search when filters change (but not on initial mount)
    if (filters.state || filters.billtype || filters.license_start_date || filters.license_end_date || filters.created_start_date || filters.created_end_date) {
      load();
    }
  }, [filters.state, filters.billtype, filters.license_start_date, filters.license_end_date, filters.created_start_date, filters.created_end_date]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({ ...DEFAULT_FILTERS });
    // Clear search field and reload data
    setSearch("");
    load(true);
  };

  function toggleSort(sortKey) {
    if (sortColumn !== sortKey) {
      setSortColumn(sortKey);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else {
      setSortColumn(null);
      setSortDirection("asc");
    }
  }

  const sortedRecords = sortColumn
    ? [...records].sort((a, b) => {
        const aVal = getSortValue(a, sortColumn);
        const bVal = getSortValue(b, sortColumn);
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      })
    : records;

  const columns = 10;
  return (
    <PageLayout>
      <style>{filterBarStyles}</style>
      <style>{tableStyles}</style>
      <h1>Clients</h1>
      <div className="hr hr--darkest"></div>

      <div className="filter-bar">
        <div className="filter-bar__field">
          <span className="filter-bar__label">Search</span>
          <input
            className="filter-bar__input"
            placeholder="Search by name or code..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
          />
        </div>
        <div className="filter-bar__field filter-bar__field--narrow">
          <span className="filter-bar__label">State</span>
          <select
            className="filter-bar__input"
            value={filters.state}
            onChange={e => handleFilterChange('state', e.target.value)}
          >
            <option value="">All States</option>
            <option value="AL">AL</option>
            <option value="AK">AK</option>
            <option value="AZ">AZ</option>
            <option value="AR">AR</option>
            <option value="CA">CA</option>
            <option value="CO">CO</option>
            <option value="CT">CT</option>
            <option value="DE">DE</option>
            <option value="FL">FL</option>
            <option value="GA">GA</option>
            <option value="HI">HI</option>
            <option value="ID">ID</option>
            <option value="IL">IL</option>
            <option value="IN">IN</option>
            <option value="IA">IA</option>
            <option value="KS">KS</option>
            <option value="KY">KY</option>
            <option value="LA">LA</option>
            <option value="ME">ME</option>
            <option value="MD">MD</option>
            <option value="MA">MA</option>
            <option value="MI">MI</option>
            <option value="MN">MN</option>
            <option value="MS">MS</option>
            <option value="MO">MO</option>
            <option value="MT">MT</option>
            <option value="NE">NE</option>
            <option value="NV">NV</option>
            <option value="NH">NH</option>
            <option value="NJ">NJ</option>
            <option value="NM">NM</option>
            <option value="NY">NY</option>
            <option value="NC">NC</option>
            <option value="ND">ND</option>
            <option value="OH">OH</option>
            <option value="OK">OK</option>
            <option value="OR">OR</option>
            <option value="PA">PA</option>
            <option value="RI">RI</option>
            <option value="SC">SC</option>
            <option value="SD">SD</option>
            <option value="TN">TN</option>
            <option value="TX">TX</option>
            <option value="UT">UT</option>
            <option value="VT">VT</option>
            <option value="VA">VA</option>
            <option value="WA">WA</option>
            <option value="WV">WV</option>
            <option value="WI">WI</option>
            <option value="WY">WY</option>
          </select>
        </div>
        <div className="filter-bar__field filter-bar__field--narrow">
          <span className="filter-bar__label">Bill Type</span>
          <select
            className="filter-bar__input"
            value={filters.billtype}
            onChange={e => handleFilterChange('billtype', e.target.value)}
          >
            <option value="">All</option>
            <option value="Annual">Annual</option>
            <option value="Semi-Annual">Semi-Annual</option>
            <option value="Quarterly">Quarterly</option>
            <option value="Monthly">Monthly</option>
          </select>
        </div>
        <div className="filter-bar__field">
          <span className="filter-bar__label">Desktop Version</span>
          <input
            className="filter-bar__input"
            placeholder="19.1.0"
            value={filters.desktop_version}
            onChange={e => handleFilterChange('desktop_version', e.target.value)}
          />
        </div>
        <div className="filter-bar__field">
          <span className="filter-bar__label">License Start</span>
          <input
            className="filter-bar__input"
            type="date"
            value={filters.license_start_date}
            onChange={e => handleFilterChange('license_start_date', e.target.value)}
          />
        </div>
        <div className="filter-bar__field">
          <span className="filter-bar__label">License End</span>
          <input
            className="filter-bar__input"
            type="date"
            value={filters.license_end_date}
            onChange={e => handleFilterChange('license_end_date', e.target.value)}
          />
        </div>
        <div className="filter-bar__field">
          <span className="filter-bar__label">Created Start</span>
          <input
            className="filter-bar__input"
            type="date"
            value={filters.created_start_date}
            onChange={e => handleFilterChange('created_start_date', e.target.value)}
          />
        </div>
        <div className="filter-bar__field">
          <span className="filter-bar__label">Created End</span>
          <input
            className="filter-bar__input"
            type="date"
            value={filters.created_end_date}
            onChange={e => handleFilterChange('created_end_date', e.target.value)}
          />
        </div>
        <div className="filter-bar__field" style={{ flex: "0 0 auto" }}>
          <span className="filter-bar__label" style={{ visibility: "hidden" }}>&nbsp;</span>
          <div style={{ display: "flex", gap: 10 }}>
            <ButtonGroup>
              <Button onClick={load}>
                Search
              </Button>
            </ButtonGroup>
            <button className="filter-bar__clear" onClick={clearFilters} title="Clear filters">
              <XMarkIcon style={{ width: '16px', height: '16px', display: 'inline-block', verticalAlign: 'middle' }} />
            </button>
            <button
              className="filter-bar__clear"
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh and clear cache"
            >
              <ArrowPathIcon style={{ width: '16px', height: '16px', display: 'inline-block', verticalAlign: 'middle' }} />
            </button>
            <button
              className="filter-bar__clear"
              onClick={exportAllRecordsAsCSV}
              disabled={records.length === 0}
            >
              Export
            </button>
            <button
              className="filter-bar__clear"
              onClick={copyAllUUIDsToClipboard}
              disabled={records.length === 0 || copyStatus !== "idle"}
              title="Copy all UUIDs to clipboard (one per line)"
            >
              {copyStatus === "copying" ? "Copying..." : copyStatus === "copied" ? "Copied!" : "Copy UUIDs"}
            </button>
          </div>
        </div>
        {loading && (
          <div className="loading-container" style={{ alignSelf: "flex-end", marginBottom: 4 }}>
            <div className="loading-icon"></div>
          </div>
        )}
      </div>

      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 6 }}>
        {records.length} of {total} loaded
        {sortColumn && records.length < total && (
          <span style={{ marginLeft: 8, color: "#9ca3af" }}>
            (sorting applies to loaded rows only — scroll to load more)
          </span>
        )}
      </div>

      <div
        className="clients-table"
        style={{
          background: "#ffffff",
          borderRadius: 8,
          outline: "1px solid #e5e7eb",
          height: "calc(100vh - 340px)",
        }}
      >
        <InfiniteLoader
          isRowLoaded={isRowLoaded}
          loadMoreRows={loadMoreRows}
          rowCount={total}
        >
          {({ onRowsRendered, registerChild }) => (
            <AutoSizer>
              {({ height, width }) => {
                return (
                  <Table
                    headerHeight={40}
                    width={width}
                    height={height}
                    rowCount={total}
                    rowHeight={40}
                    disableHeader={false}
                    rowGetter={({ index }) => sortedRecords[index] || {}}
                    onRowsRendered={onRowsRendered}
                    ref={registerChild}
                  >
                    <Column
                    label="Name"
                    dataKey="name"
                    width={width / columns}
                    headerRenderer={() => <SortableHeader label="Name" sortKey="name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.uuid) {
                        return "Loading...";
                      }
                      return (rowData.name);
                    }}
                  />
                  <Column
                    label="Code"
                    dataKey="code"
                    width={width / columns / 2}
                    headerRenderer={() => <SortableHeader label="Code" sortKey="code" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!cellData) {
                        return "Loading....";
                      }
                      return cellData;
                    }}
                  />
                  <Column
                    label="State"
                    dataKey="permissions"
                    width={width / columns / 2}
                    headerRenderer={() => <SortableHeader label="State" sortKey="state" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.uuid) {
                        return "Loading...";
                      }
                      return rowData.permissions?.state || "-";
                    }}
                  />
                  <Column
                    label="Bill Type"
                    dataKey="permissions"
                    width={width / columns / 2}
                    headerRenderer={() => <SortableHeader label="Bill Type" sortKey="billtype" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.uuid) {
                        return "Loading...";
                      }
                      return rowData.permissions?.billtype || "-";
                    }}
                  />
                  <Column
                    label="Cloud Type"
                    dataKey="cloud_type"
                    width={width / columns / 2}
                    headerRenderer={() => <SortableHeader label="Cloud Type" sortKey="cloud_type" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.uuid) {
                        return "Loading...";
                      }
                      const cloudType = getCloudType(rowData);
                      return cloudType ? (CLOUD_TYPE_SHORT_LABELS[cloudType] || cloudType) : "-";
                    }}
                  />
                  <Column
                    label="Cloud Users"
                    dataKey="cloud_users"
                    width={width / columns / 2}
                    headerRenderer={() => <SortableHeader label="Cloud Users" sortKey="cloud_users" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.uuid) {
                        return "Loading...";
                      }
                      const cloudUsers = getCloudUsers(rowData);
                      return cloudUsers != null ? cloudUsers : "-";
                    }}
                  />
                  <Column
                    label="Desktop Version"
                    dataKey="desktop_version"
                    width={width / columns}
                    headerRenderer={() => <SortableHeader label="Desktop Version" sortKey="desktop_version" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!rowData.uuid) {
                        return "Loading...";
                      }
                      return cellData || "-";
                    }}
                  />
                  <Column
                    label="License Date"
                    dataKey="permissions"
                    width={width / columns}
                    headerRenderer={() => <SortableHeader label="License Date" sortKey="license_date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.uuid) {
                        return "Loading...";
                      }
                      return rowData.permissions?.license_date || "-";
                    }}
                  />
                  <Column
                    label="Status"
                    dataKey="enabled"
                    width={width / columns / 2}
                    headerRenderer={() => <SortableHeader label="Status" sortKey="status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.uuid) {
                        return "Loading...";
                      }
                      return <StatusBadge enabled={rowData.enabled} />;
                    }}
                  />
                  <Column
                    label="Created"
                    dataKey="created_at"
                    width={width / columns}
                    headerRenderer={() => <SortableHeader label="Created" sortKey="created_at" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!rowData.uuid) {
                        return "Loading...";
                      }
                      return new Date(cellData).toLocaleString()
                    }}
                  />
                  <Column
                    label="Updated"
                    dataKey="updated_at"
                    width={width / columns}
                    headerRenderer={() => <SortableHeader label="Updated" sortKey="updated_at" sortColumn={sortColumn} sortDirection={sortDirection} onSort={toggleSort} />}
                    cellRenderer={({ rowData, cellData }) => {
                      if (!rowData.uuid) {
                        return "Loading...";
                      }
                      return cellData ? new Date(cellData).toLocaleString() : "-";
                    }}
                  />
                  <Column
                    label=""
                    dataKey="name"
                    width={width / 6}
                    cellRenderer={({ rowData }) => {
                      if (!rowData.uuid) {
                        return "Loading...";
                      }
                      return (
                        <div style={{ alignItems: "right" }}>
                          <ButtonGroup>
                            <Link className="button m-bot-0" to={`/client/${rowData.uuid}/`}>
                              View
                            </Link>
                          </ButtonGroup>
                        </div>
                      );
                    }}
                  />
                  </Table>
                );
              }}
            </AutoSizer>
          )}
        </InfiniteLoader>
      </div>
    </PageLayout>
  );
}